import Alpine from "alpinejs";

const endpointUrl = "https://api.submarineplatform.com/graphql";
const tokenUrl = "/apps/platform/tokens";
const formatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});


async function graphqlFetch(query: string, variables: Record<string, any>) {
  const response = await fetch(endpointUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization":`Bearer ${await fetchToken()}`
    },
    body: JSON.stringify({ query, variables }),
  });
  return await response.json();
}

async function storefrontFetch(query: string, variables: Record<string, any> = {}) {
  const response = await fetch(window.Shopify.routes.root + 'api/2023-10/graphql.json', {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": await getStorefrontAccessToken(),
    },
    body: JSON.stringify({ query, variables }),
  });
  return await response.json();
}

async function getStorefrontAccessToken(): Promise<string> {
  if (window.Shopify?.storefrontAccessToken) {
    return window.Shopify.storefrontAccessToken;
  }
  
  throw new Error('Shopify Storefront Access Token not found');
}

function daysUntilDate(dateString: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const targetDate = new Date(dateString);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

async function fetchToken() {
  try {
    const response = await fetch(tokenUrl, {
      method: "GET",
      mode: "cors",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.text();
  } catch (error) {
    
  }
}

export function initSubmarineComponent() {
  Alpine.data("submarineManageSubscription", (subscriptionId: String, marketHandle: string = '') => {
    return {
      feedbackModal: {} as { title: string; content: string; link: string; open: boolean },
      getDate(myDate: Date) {
        const date = new Date(myDate);
        if (isNaN(date.getTime())) {
          return '';
        }
        return formatter.format(date);
      },
      paymentMethodUpdateEmailSent: false,
      loading: true,
      updateAddressLoading: false,
      openAddressUpdateModal: false,
      toggleOpenAddressUpdateModal() {
        this.openAddressUpdateModal = !this.openAddressUpdateModal;
      },
      _flavorOptionsByProduct: {} as Record<string, Promise<any[]>>,
      subscription: {} as {
        id: string;
        order: {
          name: string;
          externalId: string;
        };
        lines: any[];
        customAttributes: {
          name: string;
          value: string;
        }[];
        lastProcessedOrder: Date;
        nextBillingAt: Date;
        paymentMethod: {
          activePaymentInstrument: {
            paymentSource: {
              last4: string;
            };
          };
        };
        deliveryMethod: {
          address: {
            city: string;
            country: {
              name: string;
            };
            firstName: string;
            lastName: string;
            phone: string;
            postcode: string;
            province: {
              code: string;
            };
            street1: string;
            street2: string;
          };
        };
        nextScheduledOrder: {
          paymentIntent: {
            amount: {
              amount: string;
              currency: string;
            };
          };
        };
        [key: string]: any;
      },
      marketHandle: marketHandle,
      email: "",
      activePlans() {
        return this.subscription?.availableSubscriptionPlans?.filter(p => p.status === 'ACTIVE') || [];
      },
      async processSubscriptionOrder() {
        this.loading = true;
        const response = await graphqlFetch(PROCESS_SUBSCRIPTION_ORDER, {
          input: { id: this.subscription.nextScheduledOrder.id },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionOrderProcess.subscriptionOrder.subscription;
          this.feedbackModal = {
            title: "Order Processed",
            content: `<div>Your order has been processed and will be pick, packed & shipped as soon as possible</div><div>You will find the order in your <a href="/account" class="underline">Order History</a> page</div>`,
            link: "/account",
            linkText: "Order History",
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Processing Failed",
            content: `<div>There was an error processing this order. <br/> Please contact support</div>`,
            open: true,
          };
        }

        this.loading = false;
      },
      async setSchedule(event: Event) {
        this.loading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        const response = await graphqlFetch(SET_SUBSCRIPTION_SCHEDULE, {
          input: {
            id: this.subscription.id,
            nextDeliveryAt: formData.get("date"),
            subscriptionAnchorId: this.subscription.subscriptionAnchor.id,
            subscriptionPlanId: this.subscription.subscriptionPlan.id,
          },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionSetSchedule.subscription;
          this.feedbackModal = {
            title: "Order Rescheduled",
            content: `<div>Your order has been rescheduled to ${formData.get("date")}</div>`,
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Rescheduling Failed",
            content: `<div>There was an error rescheduling this order. <br/> Please contact support</div>`,
            open: true,
          };
        }
        this.loading = false;
      },
      async changeFrequency(event: Event) {
        this.loading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        const { selectedPlan } = JSON.parse(formData.get("deliveryFrequency"));
        if (this.subscription.subscriptionAnchor.id != selectedPlan.anchorId) {
          const response = await graphqlFetch(SET_SUBSCRIPTION_SCHEDULE, {
            input: {
              id: this.subscription.id,
              nextDeliveryAt: this.subscription.nextScheduledOrder.expectedDeliveryAt,
              subscriptionAnchorId: selectedPlan.anchorId,
              subscriptionPlanId: selectedPlan.planId,
            },
          });
          try {
          const { data } = response;
          this.subscription = data.subscriptionSetSchedule.subscription;

          await this.attachFlavorsToLines();

          this.feedbackModal = {
            title: "Order Frequency Updated",
            content: `<div>Your delivery frequency has been changed to ${selectedPlan.description}.`,
            open: true,
          };
          }
          catch {
            this.feedbackModal = {
              title: "Delivery Frequency Update Failed",
              content: `<div>There was an error editing the delivery frequency with this subscription. <br/> Please contact support</div>`,
              open: true,
            };
          }
        }
        this.loading = false;
      },
      async skipSubscriptionOrder() {
        this.loading = true;
        const response = await graphqlFetch(SKIP_SUBSCRIPTION_ORDER, {
          input: { id: this.subscription.nextScheduledOrder.id },
        });
        try {
          const { data } = response;
          this.subscription = data.subscriptionOrderSkip.subscriptionOrder.subscription;
          this.feedbackModal = {
            title: "Order Skipped",
            content: `<div>Your next order has been skipped</div>`,
            open: true,
          };
        } catch {
          this.feedbackModal = {
            title: "Order Skipping Failed",
            content: `<div>Your order has not been skipped <br/> Please contact support</div>`,
            open: true,
          };
        }
        this.loading = false;
      },
      async sendPaymentMethodUpdateEmail() {
        const response = await graphqlFetch(SEND_PAYMENT_METHOD_UPDATE_EMAIL, {
          input: { id: this.subscription.paymentMethod.id },
        });
        const { data } = response;
        if (data.paymentMethodSendUpdateEmail.userErrors.length > 0) {
          
        } else {
          this.paymentMethodUpdateEmailSent = true;
        }
      },
      async allVariantAddOne(): Promise<void> {
        interface LineItem {
          productVariantId: string;
          quantity: number;
        }

        if (!this.subscription?.lines || this.subscription.lines.length === 0) return;

        const lines: LineItem[] = this.subscription.lines.map((lineItem: any) => ({
          productVariantId: `gid://external/ProductVariant/${lineItem.productVariant.externalId}`,
          quantity: lineItem.quantity + 1,
        }));

        interface SubscriptionUpdateResponse {
          data: {
            subscriptionUpdate: {
              subscription: typeof this.subscription;
            };
          };
        }

        const response: SubscriptionUpdateResponse = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
          input: {
            id: this.subscription.id,
            lines,
          },
        });

        if (response?.data?.subscriptionUpdate?.subscription) {
          this.subscription = response.data.subscriptionUpdate.subscription;

          await this.attachFlavorsToLines();
          await this.syncSubscriptionFlavors(undefined, lineItem.id);
        }
      },
      async syncSubscriptionFlavors(customFlavorData?: any[], lineId?: string): Promise<void> {
        try {
          const normalize = (flavor: any) => ({
            id: flavor.id,
            quantity: flavor.quantity,
            title: flavor.title,
          });

          const lines = this.subscription?.lines || [];
          // Resolve the target line: explicit lineId, else the first line (legacy).
          const targetLine = lineId
            ? lines.find((l: any) => l.id === lineId || this.lineIdSuffix(l.id) === this.lineIdSuffix(lineId))
            : lines[0];

          // Build the per-line map from each line's currentFlavors, overriding
          // the target line with the freshly-selected flavors when provided.
          const flavorsByLine: Record<string, any[]> = {};
          lines.forEach((line: any) => {
            const isTarget = targetLine && line.id === targetLine.id;
            const flavors = isTarget && customFlavorData
              ? customFlavorData
              : (line.currentFlavors || []);
            if (flavors && flavors.length > 0) {
              flavorsByLine[this.lineIdSuffix(line.id)] = flavors.map(normalize);
            }
          });

          if (Object.keys(flavorsByLine).length > 0) {
            // Persist flavors directly on the Submarine subscription's
            // customAttributes via the GraphQL SubscriptionUpdate mutation.
            // Preserve any existing attributes (all line items' data) and
            // overwrite the bundleFlavorsByLine entry with the fresh map.
            const existingAttrs = this.subscription.customAttributes || [];
            const customAttributes = existingAttrs
              .filter(
                (a: any) =>
                  a.name !== 'bundleFlavorsByLine' && a.name !== 'bundleFlavors',
              )
              .map((a: any) => ({ name: a.name, value: a.value }));
            customAttributes.push({
              name: 'bundleFlavorsByLine',
              value: JSON.stringify(flavorsByLine),
            });

            const response = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
              input: {
                id: this.subscription.id,
                customAttributes,
                overwriteChildCustomAttributes: true,
              },
            });

            const userErrors = response?.data?.subscriptionUpdate?.userErrors;
            if (userErrors && userErrors.length > 0) {
              throw new Error(userErrors[0]?.message || 'Failed to sync flavors');
            }

            if (response?.data?.subscriptionUpdate?.subscription) {
              this.subscription = response.data.subscriptionUpdate.subscription;
            }
          }
        } catch (error) {
          throw error;
        }
      },
      async getFlavorOptionsForSwapping(currentFlavorId: string, productExternalId: string): Promise<any[]> {
        const allOptions = await this.getFlavorOptionsFromMetafields(productExternalId);

        const currentFlavor = allOptions.find(option => option.id === currentFlavorId);

        if (!currentFlavor) {
          return allOptions.filter(option => option.availableForSale);
        }

        return allOptions.filter(
          option =>
            option.productId === currentFlavor.productId &&
            (option.availableForSale || option.id === currentFlavorId),
        );
      },
      // Fetches the full flavor-option set for the bundle. Memoized at the
      // component level so repeated calls (per line, per swap dropdown) reuse a
      // single Storefront round-trip instead of re-fetching.
      async getFlavorOptionsFromMetafields(productExternalId?: string): Promise<any[]> {
        if (!productExternalId) {
          return [];
        }
        if (!this._flavorOptionsByProduct[productExternalId]) {
          this._flavorOptionsByProduct[productExternalId] = this._fetchFlavorOptions(productExternalId);
        }
        return await this._flavorOptionsByProduct[productExternalId];
      },
      async _fetchFlavorOptions(productExternalId: string): Promise<any[]> {
        try {
          if (!productExternalId) {
            return [];
          }

          // product.externalId is a plain Shopify product id (same as
          // productVariant.externalId is for variants), so we can build the
          // Storefront gid directly and skip the variant -> product lookup.
          const productId = `gid://shopify/Product/${productExternalId}`;

          const countryCode = window.localization?.country?.isoCode;

          const query = `
            query GetProductWithBundlingById(
              $id: ID!
              ${countryCode ? '$country: CountryCode!' : ''}
            )
              ${countryCode ? '@inContext(country: $country)' : ''} {
              product(id: $id) {
                id
                handle
                title
                metafield(namespace: "bundling", key: "step_1_products") {
                  value
                  reference {
                    ... on Product {
                      id
                      variants(first: 50) {
                        edges {
                          node {
                            id
                            title
                            availableForSale
                            image {
                              url(transform: { maxWidth: 400 })
                            }
                          }
                        }
                      }
                    }
                  }
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url(transform: { maxWidth: 400 })
                                }
                                metafield(namespace: "liquid_objects", key: "unavailable_override") {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                step2: metafield(namespace: "bundling", key: "step_2_products") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url(transform: { maxWidth: 400 })
                                }
                                metafield(namespace: "liquid_objects", key: "unavailable_override") {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                step3: metafield(namespace: "bundling", key: "step_3_products") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url(transform: { maxWidth: 400 })
                                }
                                metafield(namespace: "liquid_objects", key: "unavailable_override") {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                step4: metafield(namespace: "bundling", key: "step_4_products") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url(transform: { maxWidth: 400 })
                                }
                                metafield(namespace: "liquid_objects", key: "unavailable_override") {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
                step5: metafield(namespace: "bundling", key: "step_5_products") {
                  value
                  references(first: 10) {
                    edges {
                      node {
                        ... on Product {
                          id
                          variants(first: 50) {
                            edges {
                              node {
                                id
                                title
                                availableForSale
                                image {
                                  url(transform: { maxWidth: 400 })
                                }
                                metafield(namespace: "liquid_objects", key: "unavailable_override") {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `;

          const variables: Record<string, any> = {
            id: productId,
          };

          if (countryCode) {
            variables.country = countryCode;
          }

          const response = await storefrontFetch(query, variables);
          
          if (response.errors) {
            console.error('Storefront API errors:', response.errors);
            throw new Error('Failed to fetch product data from Storefront API');
          }

          const product = response.data?.product;
          if (!product) {
            throw new Error('Product not found');
          }

          let allVariants: any[] = [];
          const marketHandle = this.marketHandle;
          const steps = ['metafield', 'step2', 'step3', 'step4', 'step5'];
          
          steps.forEach(stepKey => {
            const stepData = product[stepKey];
            if (stepData?.references?.edges) {
              stepData.references.edges.forEach((productEdge: any) => {
                const bundleProduct = productEdge.node;
                if (bundleProduct?.variants?.edges) {
                  bundleProduct.variants.edges.forEach((variantEdge: any) => {
                    const variant = variantEdge.node;
                    const unavailableOverride = variant.metafield?.value || '';
                    if (!marketHandle || !unavailableOverride.includes(marketHandle)) {
                      allVariants.push({
                        id: variant.id.replace('gid://shopify/ProductVariant/', ''),
                        title: variant.title,
                        imageUrl: variant.image?.url,
                        productId: bundleProduct.id.replace('gid://shopify/Product/', ''),
                        availableForSale: !!variant.availableForSale,
                      });
                    }
                  });
                }
              });
            }
          });

          return allVariants;
        } catch (error) {
          console.error('Error fetching flavor options from Storefront API:', error);

          return [];
        }
      },
      lineIdSuffix(lineId: string): string {
        return lineId?.split('/').pop() || '';
      },
      async attachFlavorsToLines(): Promise<void> {
        const lines = this.subscription?.lines;
        if (!lines || lines.length === 0) return;

        const attrs = this.subscription.customAttributes || [];
        const getAttr = (name: string) =>
          attrs.find((a: any) => a.name === name)?.value;

        let byLine: Record<string, any[]> | null = null;
        let legacy: any[] | null = null;
        const byLineRaw = getAttr('bundleFlavorsByLine');
        const legacyRaw = getAttr('bundleFlavors');
        try {
          byLine = byLineRaw ? JSON.parse(byLineRaw) : null;
        } catch {}
        try {
          legacy = legacyRaw ? JSON.parse(legacyRaw) : null;
        } catch {}

        // options + imageUrl from metafields, resolved per line from that
        // line's own product. Lines sharing a product reuse a single fetch
        // thanks to the productId-keyed cache in getFlavorOptionsFromMetafields.
        this.subscription.lines = await Promise.all(
          lines.map(async (line: any, idx: number) => {
            const flavorOptions = await this.getFlavorOptionsFromMetafields(
              line.product?.externalId,
            );
            
            const fallbackImageUrl =
              line.productVariant?.imageUrl || line.product?.imageUrl || null;
            const enrich = (arr: any[] = []) =>
              arr.map((f: any) => {
                const option = flavorOptions?.find((o: any) => o.id === f.id);
                return {
                  ...f,
                  imageUrl: f.imageUrl || option?.imageUrl || fallbackImageUrl,
                  isOrphaned: flavorOptions.length > 0 && !option,
                };
              });

            let flavors: any[] = [];
            if (byLine) {
              flavors = byLine[this.lineIdSuffix(line.id)] || [];
            } else if (legacy && idx === 0) {
              flavors = legacy; // legacy format only applies to the first line
            }
            return {
              ...line,
              currentFlavors: enrich(flavors),
              flavorsOptions: flavorOptions || [],
              flavourError: null,
            };
          }),
        );
      },
      async updateAddress(event: Event) {
        this.updateAddressLoading = true;
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);

        const { data } = await graphqlFetch(SUBSCRIPTION_UPDATE_MUTATION_OBJECT, {
          input: {
            id: this.subscription.id,
            deliveryMethod: {
              shipping: {
                address: {
                  city: formData.get("city"),
                  country: {
                    code: formData.get("country"),
                  },
                  firstName: formData.get("firstName"),
                  lastName: formData.get("lastName"),
                  postcode: formData.get("postcode"),
                  province: {
                    code: formData.get("province"),
                  },
                  street1: formData.get("street1"),
                  street2: formData.get("street2"),
                  phone: formData.get("phone"),
                },
              },
            },
          },
        });

        this.subscription = data.subscriptionUpdate.subscription;
        this.openAddressUpdateModal = false;
        this.updateAddressLoading = false;
      },
      async cancelSubscription(event: Event) {
        this.loading = true;
        const { data } = await graphqlFetch(CANCEL_SUBSCRIPTION, {
          input: {
            id: this.subscription.id,
          },
        });

        this.subscription = data.subscriptionCancel.subscription;
        this.loading = false
        this.feedbackModal = {
          title: "Subscription Cancelled",
          content: `<div>The subscription has been cancelled. <br/> Please contact support if this was a mistake.</div>`,
          open: true,
        };
      },
      async init() {
        try {
          const response = await graphqlFetch(SUBSCRIPTION_QUERY, { subscriptionId });
          if (response?.data?.subscription) {
            this.subscription = response.data.subscription;
            this.address = response.data.subscription.deliveryMethod?.address || {};
            await this.attachFlavorsToLines();
          }
        } catch (error) {
          
        } 
        
        this.loading = false;
        this.updateAddressLoading = false;
      },
    };
  });

  Alpine.data("submarineGetSubscriptions", (customerId: String) => {
    return {
      loading: true,
      loadingMore: false,
      hasNextPage: false,
      endCursor: null as string | null,
      subscriptions: [] as {
        id: string;
        order: {
          name: string;
          externalId: string;
        };
        nextScheduledOrder: {
          expectedDeliveryAt: Date;
          status: string;
          paymentIntent: {
            amount: {
              amount: string;
              currency: string;
            };
          };
        };
        daysToNextScheduledOrder: number;
        subscriptionAnchor: {
          frequency: string;
        };
      }[],
      daysUntilDate(dateString: string): number {
        formatter.format(new Date(dateString))
        const today = new Date();
        today.setHours(0, 0, 0, 0);
      
        const targetDate = new Date(dateString);
        targetDate.setHours(0, 0, 0, 0);
      
        const diffTime = targetDate.getTime() - today.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      
        return diffDays;
      },
      async fetchSubscriptions(after: string | null = null) {
        const response = await graphqlFetch(
          `
            #graphql
            query($customerIds: [SharedGlobalID!], $after: String){
                searchSubscriptions(customerIds: $customerIds) {
                    subscriptions(first: 24, after: $after) {
                        nodes {
                            id
                            order {
                                externalId
                                name
                            }
                            nextScheduledOrder {
                                expectedDeliveryAt
                                status
                                paymentIntent {
                                    amount {
                                        amount
                                        currency
                                    }
                                }
                            }
                            subscriptionAnchor {
                                frequency: name
                            }
                            status
                        }
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                    }
                }
            }`,
          { customerIds: [customerId], after },
        );

        const result = response?.data?.searchSubscriptions?.subscriptions;
        return {
          nodes: result?.nodes || [],
          hasNextPage: result?.pageInfo?.hasNextPage || false,
          endCursor: result?.pageInfo?.endCursor || null,
        };
      },
      async loadMore() {
        if (!this.hasNextPage || this.loadingMore) return;
        this.loadingMore = true;
        try {
          const { nodes, hasNextPage, endCursor } = await this.fetchSubscriptions(this.endCursor);
          this.subscriptions = [...this.subscriptions, ...nodes];
          this.hasNextPage = hasNextPage;
          this.endCursor = endCursor;
        } catch (error) {
          // silently fail on load more
        }
        this.loadingMore = false;
      },
      async init() {
        try {
          const { nodes, hasNextPage, endCursor } = await this.fetchSubscriptions();
          this.subscriptions = nodes;
          this.hasNextPage = hasNextPage;
          this.endCursor = endCursor;
        } catch (error) {
          this.subscriptions = [];
        }
        
        this.loading = false;
      },
    };
  });

  Alpine.data("submarine", () => {
    return {
      loading: true,
      subscriptions: [] as { id: string; name: string; status: string }[],
      async init() {
        const { data } = await graphqlFetch(
          /* GraphQL */ `
            query {
              subscriptionPlans {
                nodes {
                  id
                  name
                  status
                }
              }
            }
          `,
          {},
        );

        this.subscriptions = data.subscriptionPlans.nodes;
        this.loading = false;
      },
    };
  });
}

/**
 <div x-data="submarine()" class="my-12 bg-blue-100 p-4">
 <p>Submarine:</p>

 <template x-if="loading">
 <p>Loading...</p>
 </template>

 <ul>
 <template x-for="subscription in subscriptions">
 <li>
 <p x-text="subscription.id"></p>
 <p x-text="subscription.name"></p>
 <p x-text="subscription.status"></p>
 </li>
 </template>
 </ul>
 </div>
 */

export const SUBSCRIPTION_FRAGMENT = `#graphql
 fragment SubscriptionDetails on Subscription {
 status
  availableSubscriptionPlans {
      id
      status
      anchors {
        id
        position
      }
      frequency {
        description
      }
    }
  subscriptionPlan {
    id
  }
  subscriptionAnchor {
    id
  }
  id
  processedSubscriptionOrdersCount
  order {
    externalId
    name
  }
  lines {
    id
    quantity
    productVariant {
      externalId
      title
      imageUrl
    }
    product {
      externalId
      productVariants {
        imageUrl
        externalId
        title
      }
      title
    }
  }
  customAttributes {
    name
    value
  }
  customAttributes {
    name
    value
  }
  lastProcessedOrder {
    processedAt
    expectedBillingAt
  }
  nextBillingAt
  paymentMethod {
  id
    activePaymentInstrument {
      paymentSource {
        ... on Card {
          last4
          brand
        }
      }
    }
  }
  deliveryMethod {
    ... on SubscriptionDeliveryMethodShipping {
      address {
        city
        country {
          name
          code
        }
        firstName
        lastName
        phone
        postcode
        province {
          code
        }
        street1
        street2
      }
    }
  }
  nextScheduledOrder {

    nextSubscriptionOrder {
        expectedDeliveryAt
      }
    id
    lastPaymentMethodUpdateEvent {
      action
      createdAt
      description
    }
    expectedDeliveryAt
    paymentIntent {
      amount {
        amount
        currency
      }
    }
    financials {
      totalPrice {
        amount
      }
      subtotal {
        amount
      }
      discounts {
        total {
          amount
        }
      }
    }
  }
}`;

const SUBSCRIPTION_QUERY = `#graphql
${SUBSCRIPTION_FRAGMENT}
query SearchSubscriptions($subscriptionId: GlobalID!) {
  subscription(id: $subscriptionId) {
    ...SubscriptionDetails
  }
}`;

const SUBSCRIPTION_UPDATE_MUTATION_OBJECT = `#graphql
${SUBSCRIPTION_FRAGMENT}
mutation SubscriptionMutation($input: SubscriptionUpdateInput!) {
  subscriptionUpdate(input: $input) {
    subscription {
      ...SubscriptionDetails
    }
    userErrors {
      field
      message
    }
  }
}`;

const CANCEL_SUBSCRIPTION = `#graphql
${SUBSCRIPTION_FRAGMENT}
  mutation subscriptionCancel($input: SubscriptionCancelInput!) {
    subscriptionCancel(input: $input) {
      subscription {
        ...SubscriptionDetails
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const PROCESS_SUBSCRIPTION_ORDER = `#graphql
${SUBSCRIPTION_FRAGMENT}
  mutation SubscriptionOrderProcess($input: SubscriptionOrderProcessInput!) {
    subscriptionOrderProcess(input: $input) {
      subscriptionOrder {
        id
        paymentStatus
        status
        subscription {
          ...SubscriptionDetails
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;
export const SKIP_SUBSCRIPTION_ORDER = `#graphql
 ${SUBSCRIPTION_FRAGMENT}
  mutation subscriptionOrderSkip($input: SubscriptionOrderSkipInput!) {
    subscriptionOrderSkip(input: $input) {
      subscriptionOrder {
        id
        status
        subscription {
          ...SubscriptionDetails
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export const PAYMENT_METHOD_DETAILS = `
  activePaymentInstrument {
    description
    paymentSource {
      ... on Card {
        brand
        expiry {
          month
          year
        }
        last4
      }
    }
  }
  externalId
  id
  status
`;

export const GET_PAYMENT_METHOD = `#graphql
  query paymentMethod($id: GlobalID!) {
    paymentMethod(id: $id) {
      ${PAYMENT_METHOD_DETAILS}
    }
  }
`;

export const SEND_PAYMENT_METHOD_UPDATE_EMAIL = `#graphql
  mutation paymentMethodSendUpdateEmail($input: PaymentMethodSendUpdateEmailInput!) {
    paymentMethodSendUpdateEmail(input: $input) {
      userErrors {
        field
        message
      }
    }
  }
`;

export const SET_SUBSCRIPTION_SCHEDULE = `#graphql
 ${SUBSCRIPTION_FRAGMENT}
 mutation SubscriptionSetSchedule($input: SubscriptionSetScheduleInput!) {
  subscriptionSetSchedule(input: $input) {
    subscription {
      ...SubscriptionDetails
    }
    upcomingDeliverySlots {
      deliverAt
    }
    userErrors {
      field
      message
    }
  }
}
`;
